// src/components/ReviewsModal.js
import React, { useState, useEffect, useMemo } from 'react';
// Lightbox 및 기본 CSS import
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
// Zoom 플러그인 import (핀치 줌 등 지원)
import Zoom from "yet-another-react-lightbox/plugins/zoom";
// 선택 사항: 썸네일, 전체화면 등 다른 플러그인 추가 가능
// import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
// import "yet-another-react-lightbox/plugins/thumbnails.css";

import './ReviewsModal.css'; // 기존 모달 CSS

// --- 스크립트 URL (실제 URL로 교체 필요) ---
const REVIEWS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyF_6VsUjjEdK2xomHZE5BKWDA5rHK4HUrj8WQCD6-dtNjKk20tn3zEO-ZJ9-D2Pmnw/exec';

// StarRating 컴포넌트 (기존과 동일)
const StarRating = ({ rating }) => {
  const totalStars = 5;
  let stars = [];
  const numericRating = Math.max(0, Math.min(totalStars, parseInt(rating, 10) || 0));
  for (let i = 1; i <= totalStars; i++) {
    stars.push( <span key={i} className={`star ${i <= numericRating ? 'filled' : ''}`}>★</span> );
  }
  return <div className="star-rating">{stars}</div>;
};

function ReviewsModal({ onClose }) {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Lightbox 상태 관리 ---
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // --- 리뷰 데이터 Fetch 로직 (useEffect - 기존과 동일, 최신 10개) ---
  useEffect(() => {
    const fetchReviews = async () => {
      // ... (데이터 fetch 로직은 이전과 동일) ...
      if (REVIEWS_SCRIPT_URL === 'YOUR_GOOGLE_APPS_SCRIPT_URL_HERE') {
         setError('Google Apps Script URL을 설정해주세요.');
         setIsLoading(false);
         setReviews([ /* ... 더미 데이터 ... */ ]);
         return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(REVIEWS_SCRIPT_URL);
        if (!response.ok) throw new Error(`데이터를 불러오는 데 실패했습니다: ${response.statusText}`);
        const data = await response.json();
        if (data && Array.isArray(data.reviews)) {
           const reviewsWithImages = data.reviews.map(review => ({
               ...review,
               images: Array.isArray(review.images) ? review.images : []
           }));
           setReviews(reviewsWithImages); // .slice(0, 10)은 Apps Script에서 처리됨
        } else { throw new Error('가져온 데이터 형식이 올바르지 않습니다.'); }
      } catch (err) {
          console.error("Error fetching reviews:", err); setError(err.message); setReviews([]);
      } finally { setIsLoading(false); }
    };
    fetchReviews();
  }, []);

  // --- 배경 스크롤 방지 (useEffect - 기존과 동일) ---
  useEffect(() => {
    if (lightboxOpen) { // 라이트박스가 열렸을 때도 스크롤 방지
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = 'unset';
    }
    // 컴포넌트 언마운트 시 정리
    return () => { document.body.style.overflow = 'unset'; };
  }, [lightboxOpen]);


  // --- Lightbox에 표시할 이미지 슬라이드 목록 생성 ---
  // useMemo를 사용하여 reviews 데이터가 변경될 때만 목록을 다시 계산
  const slides = useMemo(() => {
    let allImages = [];
    reviews.forEach(review => {
      if (review.images && review.images.length > 0) {
        review.images.forEach(imgUrl => {
          allImages.push({ src: imgUrl });
        });
      }
    });
    return allImages;
  }, [reviews]);

  // --- 이미지 클릭 시 해당 이미지 인덱스를 찾아 Lightbox 열기 ---
  const openLightboxAtIndex = (reviewIndex, imageIndex) => {
    let cumulativeIndex = 0;
    for (let i = 0; i < reviewIndex; i++) {
      cumulativeIndex += reviews[i].images?.length || 0;
    }
    setLightboxIndex(cumulativeIndex + imageIndex);
    setLightboxOpen(true);
  };


  return (
    <> {/* Fragment 사용 */}
      {/* 리뷰 모달 */}
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close-button" onClick={onClose} aria-label="닫기">&times;</button>
          <h2>고객 후기</h2>
          <div className="reviews-list">
            {/* ... 로딩, 에러, 빈 상태 메시지 ... */}
            {isLoading && <p>후기를 불러오는 중...</p>}
            {error && <p style={{ color: 'red' }}>오류: {error}</p>}
            {!isLoading && !error && reviews.length === 0 && <p>표시할 후기가 없습니다.</p>}

            {/* 리뷰 목록 렌더링 */}
            {!isLoading && !error && reviews.map((review, reviewIndex) => (
              <div key={reviewIndex} className="review-item">
                {/* ... 리뷰 헤더, 내용 ... */}
                 <div className="review-header">
                    <span className="review-name">{review.name}</span>
                    <StarRating rating={review.stars} />
                    <span className="review-timeframe">{review.timeframe}</span>
                 </div>
                 <p className="review-contents">{review.contents}</p>

                {/* 이미지 썸네일 렌더링 및 클릭 핸들러 수정 */}
                {review.images && review.images.length > 0 && (
                  <div className="review-images-container">
                    {review.images.map((imageUrl, imageIndex) => (
                      <img
                        key={imageIndex}
                        src={imageUrl}
                        alt={`${review.name}님의 리뷰 이미지 ${imageIndex + 1}`}
                        className="review-image"
                        // 클릭 시 openLightboxAtIndex 호출
                        onClick={() => openLightboxAtIndex(reviewIndex, imageIndex)}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- Lightbox 컴포넌트 렌더링 --- */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={slides} // 모든 이미지를 슬라이드로 전달
        index={lightboxIndex} // 현재 선택된 이미지 인덱스
        plugins={[Zoom]} // Zoom 플러그인 활성화
        // 다른 플러그인 추가 가능: plugins={[Zoom, Thumbnails]}
        zoom={{ // Zoom 플러그인 옵션 (선택 사항)
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 500,
          doubleClickMaxStops: 2,
          keyboardMoveDistance: 50,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: false
        }}
      />
    </>
  );
}

export default ReviewsModal;